import {isString} from 'lodash';
import React, {ComponentType, JSXElementConstructor} from 'react';

// Using a symbol prevent conflicts with any key that may be added to the decorated function/component.
const keySymbol = Symbol('markdownDecorationSymbolKey');
export type DecoratedComponent<P> = ComponentType<P> & {
  [keySymbol]: symbol;
};

/*
 * This builds a decorator function and a type guard to decorate a component with a symbol and check if
 * a component has been decorated with that symbol.
 */
function buildDecoratorBuilder(idSymbol: symbol) {
  return {
    build: <P,>(Component: ComponentType<P>): ComponentType<P> & {[keySymbol]: typeof idSymbol} => {
      // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/consistent-type-assertions
      const DecoratedComponent = (props: P) => <Component {...(props as any)} />;
      DecoratedComponent[keySymbol] = idSymbol;
      return DecoratedComponent;
    },
    is: <P,>(component: JSXElementConstructor<P> | string): component is ComponentType<P> => {
      if (isString(component)) {
        return false;
      }
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return (component as ComponentType<P> & {[keySymbol]: symbol})?.[keySymbol] === idSymbol;
    },
  };
}

const markdownDecoratedSkipCountingTextContentComponentId = Symbol(
  'MarkdownDecoratedSkipCountingTextContentComponentId',
);

/**
 * Use this helper to wrap components whose content should not be counted in textContent indices for highlights.
 * When adding highlights to the JSX nodes, we detect these special components. See markdownAstToReact.tsx.
 */
export const {
  build: buildMarkdownDecoratedSkipCountingTextContentComponent,
  is: isMarkdownDecoratedSkipCountingTextContentComponent,
} = buildDecoratorBuilder(markdownDecoratedSkipCountingTextContentComponentId);

const markdownDecoratedCountChildrenTextContentComponentId = Symbol(
  'markdownDecoratedCountChildrenTextContentComponentId',
);

/**
 * Use this builder to wrap components whose content should be counted in textContent indices for highlights.
 * When adding highlights to the JSX nodes, we detect these special components. See markdownAstToReact.tsx.
 */
export const {
  build: buildMarkdownDecoratedCountChildrenTextContentComponent,
  is: isMarkdownDecoratedCountChildrenTextContentComponent,
} = buildDecoratorBuilder(markdownDecoratedCountChildrenTextContentComponentId);
